import React from "react";
import type { FlowNode } from "@learnengine/shared-types";
import { getTemplate } from "../templates/registry";

/** Friendly, addable scenes (the concept-acting library). */
const SCENE_OPTIONS: { id: string; label: string }[] = [
  { id: "car_speed_scene", label: "Speed — driving car" },
  { id: "speed_compare_scene", label: "Fast vs slow race" },
  { id: "gravity_scene", label: "Gravity — falling apple" },
  { id: "states_of_matter_scene", label: "States of matter" },
  { id: "water_cycle_scene", label: "Water cycle" },
  { id: "day_night_scene", label: "Day & night" },
  { id: "photosynthesis_scene", label: "Photosynthesis" },
  { id: "food_chain_scene", label: "Food chain" },
  { id: "fractions_scene", label: "Fractions — pizza" },
  { id: "counting_scene", label: "Counting" },
  { id: "addition_scene", label: "Addition" },
  { id: "subtraction_scene", label: "Subtraction" },
  { id: "breathing_scene", label: "Breathing — lungs" },
  { id: "electricity_scene", label: "Electricity — circuit" },
  { id: "seasons_scene", label: "Four seasons" },
  { id: "butterfly_life_scene", label: "Butterfly life cycle" },
  { id: "rainbow_scene", label: "Rainbow forming" },
  { id: "volcano_scene", label: "Volcano erupting" },
  { id: "shapes_scene", label: "Shapes" },
  { id: "telling_time_scene", label: "Telling time" },
  { id: "float_sink_scene", label: "Float vs sink" },
  { id: "magnet_scene", label: "Magnets" },
  { id: "plant_grow_scene", label: "Plant growing" },
  { id: "frog_life_scene", label: "Frog life cycle" },
  { id: "coding_sequence_scene", label: "Coding — robot follows steps" },
  { id: "coding_loop_scene", label: "Coding — loops" },
  { id: "coding_if_scene", label: "Coding — if/then" },
  { id: "debugging_scene", label: "Debugging — find the bug" },
  { id: "machine_learning_scene", label: "Machine learning" },
  { id: "neural_network_scene", label: "Neural network" },
  { id: "algorithm_scene", label: "Algorithm — recipe" },
  { id: "sorting_scene", label: "Sorting" },
  { id: "journey_scene", label: "Journey (reusable actors)" },
  { id: "xiaohei_idea_scene", label: "Hand-drawn: big idea (Xiaohei)" },
  { id: "xiaohei_walk_scene", label: "Hand-drawn: the path (Xiaohei)" },
  { id: "skin_demo_scene", label: "Skin engine demo (clean vs hand-drawn)" },
  { id: "skin_landscape_scene", label: "Skin landscape (whole-scene re-skin)" },
  { id: "actor_gallery_scene", label: "Actor gallery (rebuilt library)" },
  { id: "skin_park_scene", label: "Park scene (skin-aware)" },
  { id: "skin_counting_scene", label: "Counting lesson (skin-aware)" },
  { id: "skin_water_cycle", label: "Water cycle (skin-aware)" },
  { id: "skin_day_night", label: "Day & night (skin-aware)" },
  { id: "skin_butterfly_life", label: "Butterfly life cycle (skin-aware)" },
  { id: "skin_frog_life", label: "Frog life cycle (skin-aware)" },
  { id: "skin_photosynthesis", label: "Photosynthesis (skin-aware)" },
  { id: "skin_food_chain", label: "Food chain (skin-aware)" },
  { id: "skin_gravity", label: "Gravity (skin-aware)" },
  { id: "skin_seasons", label: "Seasons (skin-aware)" },
];

const sceneName = (id: string) =>
  SCENE_OPTIONS.find((o) => o.id === id)?.label ??
  id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export interface StoryboardEditorProps {
  topic: string;
  scenes: FlowNode[];
  onChange: (scenes: FlowNode[]) => void;
  onGenerate: () => void;
  onBack: () => void;
}

export const StoryboardEditor: React.FC<StoryboardEditorProps> = ({
  topic, scenes, onChange, onGenerate, onBack,
}) => {
  const [pt, setPt] = React.useState(0);
  const [addId, setAddId] = React.useState(SCENE_OPTIONS[0].id);

  // Shared ticker so every thumbnail animates.
  React.useEffect(() => {
    const id = window.setInterval(() => setPt((p) => (p > 1e6 ? 0 : p + 0.12)), 120);
    return () => window.clearInterval(id);
  }, []);

  const update = (i: number, patch: Partial<FlowNode>) =>
    onChange(scenes.map((n, idx) => (idx === i ? { ...n, ...patch } : n)));
  const updateProps = (i: number, patch: Record<string, unknown>) =>
    onChange(scenes.map((n, idx) => (idx === i ? { ...n, props: { ...(n.props ?? {}), ...patch } } : n)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= scenes.length) return;
    const copy = [...scenes];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const remove = (i: number) => onChange(scenes.filter((_, idx) => idx !== i));
  const add = () => {
    const node: FlowNode = {
      id: `s_${Date.now()}_${scenes.length}`,
      template: addId,
      durationSeconds: 9,
      actions: [{ type: "draw", at: 0 }],
      props: { label: sceneName(addId) },
      narrationText: "",
    };
    onChange([...scenes, node]);
  };

  const totalSecs = scenes.reduce((s, n) => s + (n.durationSeconds || 0), 0);

  return (
    <div className="storyboard">
      <div className="sb-header">
        <button className="sb-back" onClick={onBack}>← Start over</button>
        <div className="sb-title">
          <h2>Your storyboard</h2>
          <p>“{topic}” · {scenes.length} scenes · ~{totalSecs}s. Edit, reorder, add scenes — then make the video.</p>
        </div>
        <button className="sb-generate" onClick={onGenerate} disabled={scenes.length === 0}>
          Generate video ▶
        </button>
      </div>

      <div className="sb-list">
        {scenes.map((node, i) => {
          const Render = getTemplate(node.template);
          const label = String((node.props as any)?.label ?? "");
          return (
            <div className="sb-card" key={node.id}>
              <div className="sb-num">{i + 1}</div>
              <svg className="sb-thumb" viewBox="0 0 800 450" preserveAspectRatio="xMidYMid slice">
                <rect width="800" height="450" fill="#0f172a" />
                {Render ? <g>{Render({ ...(node.props ?? {}), t: pt })}</g> : null}
              </svg>
              <div className="sb-fields">
                <div className="sb-scenetype">{sceneName(node.template)}</div>
                <input
                  className="sb-caption"
                  type="text"
                  value={label}
                  placeholder="On-screen caption"
                  onChange={(e) => updateProps(i, { label: e.target.value })}
                />
                <textarea
                  className="sb-narration"
                  value={node.narrationText ?? ""}
                  placeholder="Narration (what the voice says)…"
                  rows={2}
                  onChange={(e) => update(i, { narrationText: e.target.value })}
                />
                <div className="sb-row">
                  <label className="sb-dur">
                    Length
                    <input
                      type="number" min={3} max={30}
                      value={node.durationSeconds}
                      onChange={(e) => update(i, { durationSeconds: Math.max(3, Math.min(30, Number(e.target.value) || 8)) })}
                    />s
                  </label>
                  <div className="sb-actions">
                    <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up">▲</button>
                    <button onClick={() => move(i, 1)} disabled={i === scenes.length - 1} title="Move down">▼</button>
                    <button className="sb-del" onClick={() => remove(i)} title="Delete scene">🗑</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sb-add">
        <select value={addId} onChange={(e) => setAddId(e.target.value)}>
          {SCENE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <button onClick={add}>+ Add scene</button>
      </div>
    </div>
  );
};
